// 공통 스토리지 유틸 (프로모션 온보딩 전용)
// - 24시간 만료
// - 신규 포맷 { data, timestamp } 저장/복구
// - 만료 시 관련 키 일괄 삭제 + 일관된 콘솔 로그

import { isDev } from "../config";

export const PROMOTION_DATA_KEY = "onboardingPromotionData";
export const LAST_STEP_KEY = "onboardingPromotionLastStep";
export const EXPIRATION_MS = isDev
  ? 72 * 60 * 60 * 1000
  : 72 * 60 * 60 * 1000; /*72시간*/

export interface LastStepPayload {
  type: string;
  seqByType: number;
  q?: number;
}

function now() {
  return Date.now();
}

// 현재 URL을 기준으로 온보딩 버전 식별
// - v5: "/v5-onboarding" 포함
// - promotion(기본): "/promotion-onboarding" 포함 또는 그 외
function detectVersionTag(): "v5" | "promotion" {
  try {
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    if (path.includes("/v5-onboarding")) return "v5";
    return "promotion";
  } catch (_) {
    return "promotion";
  }
}

function withVersionSuffix(baseKey: string, tag = detectVersionTag()) {
  return `${baseKey}::${tag}`;
}

// 만료 시 두 키를 모두 제거
export function clearAllPromotionStorage() {
  const tag = detectVersionTag();
  const dataKey = withVersionSuffix(PROMOTION_DATA_KEY, tag);
  const lastStepKey = withVersionSuffix(LAST_STEP_KEY, tag);
  try {
    localStorage.removeItem(dataKey);
    localStorage.removeItem(lastStepKey);
    console.log(
      `[onboardingPromotion] expired: cleared storage for version=${tag} (removed: ${dataKey}, ${lastStepKey})`
    );
  } catch (_) {
    // noop
  }
}

// Generic read with expiry for a given key
function readWithExpiry<T>(key: string): T | null {
  const saved = localStorage.getItem(key);
  if (!saved) return null;
  try {
    const raw: any = JSON.parse(saved);
    if (raw && typeof raw === "object" && "data" in raw && "timestamp" in raw) {
      const elapsed = now() - (raw.timestamp as number);
      if (elapsed < EXPIRATION_MS) {
        return raw.data as T;
      }
      // 만료 → 관련 키 전부 삭제
      clearAllPromotionStorage();
      return null;
    }
    // 레거시 포맷: 바로 데이터가 저장된 경우
    return raw as T;
  } catch (_) {
    return null;
  }
}

// Generic write with timestamp for a given key
function writeWithTimestamp<T>(key: string, value: T) {
  const wrapped = { data: value, timestamp: now() };
  localStorage.setItem(key, JSON.stringify(wrapped));
}

// Data (폼 데이터) 저장/복구
export function readPromotionData<T>(): T | null {
  const key = withVersionSuffix(PROMOTION_DATA_KEY);
  return readWithExpiry<T>(key);
}

export function writePromotionData<T>(data: T) {
  const key = withVersionSuffix(PROMOTION_DATA_KEY);
  writeWithTimestamp(key, data);
  console.log(
    `[onboardingPromotion] storage updated: ${key}`,
    JSON.stringify(data)
  );
}

// Last step 저장/복구
export function readLastStep(): LastStepPayload | null {
  const key = withVersionSuffix(LAST_STEP_KEY);
  return readWithExpiry<LastStepPayload>(key);
}

export function writeLastStep(step: LastStepPayload) {
  const key = withVersionSuffix(LAST_STEP_KEY);
  writeWithTimestamp(key, step);
  console.log(
    `[onboardingPromotion] last step updated: ${key}`,
    JSON.stringify({ data: step, timestamp: now() })
  );
}
